import { CURRENT, LOCATIONS } from "./cities";

/**
 * Booking CTA on blog posts, matched to what the reader came for.
 *
 * Every article used to ask for a mole check. That fits melanoma and nevus
 * posts, but a reader of "kaszak" or "włókniak na szyi" wants the thing
 * REMOVED, and after the October 2026 wave half the blog is that kind of post:
 * new posts drew 2.6% CTA clicks per session against 8.3% on the older,
 * screening-themed ones (GA4, 8–14.10.2026).
 *
 * The intent comes from the post's `category` (src/config/blogCategories.ts),
 * so a new article picks the right CTA without anyone remembering to set it.
 */
// "lesion" is for the reader who has a spot they cannot name — a lump, a dark
// dot, a non-healing patch, a skin-cancer article. "Zbadaj swoje znamiona"
// talks past them; they want that one lesion looked at. Same booking funnel
// as screening (a videodermoscopy visit), different words.
export type CtaIntent = "screening" | "lesion" | "removal" | "aftercare";

const INTENT_BY_CATEGORY: Record<string, CtaIntent> = {
  "zmiany-lagodne": "removal",
  "usuwanie-i-zabiegi": "removal",
  "blizny-i-rany": "aftercare",
  "nowotwory-skory": "lesion",
};

// Lesions that are normally excised rather than vaporised, plus the topics
// that live on the surgical price list (biopsy, histopathology). Their readers
// land on the surgical removal page, where that category is opened first.
const SURGICAL_TOPIC = /kaszak|tluszcz|torbiel|pilomatrix|guzek|biopsj|histopat|chirurg|wyci/;

// Cities without a laser yet (Katowice: planned within 2–3 months of
// 2026-10-07). Removal readers from there go to the surgical page.
const NO_LASER = new Set(["katowice"]);

const LASER_PAGE = "laserowe-usuwanie-znamion-pieprzykow-wlokniakow-brodawek-kurzajek";
const SURGICAL_PAGE = "usuwanie-znamion-pieprzykow";

// Closing card id — the sticky bar and the side promo scroll to it, because a
// removal booking starts with picking a city and the card holds that list.
export const CITY_PICKER_ID = "umow-zabieg";

export type CtaCity = { city: string; to: string };

export type ArticleCta = {
  intent: CtaIntent;
  overline: string;
  statsHeadline: string;
  closingHeadline: string;
  // Every intent ends with the same full-width button to the home city chooser;
  // removal and aftercare additionally list the cities above it, each chip
  // opening that city's removal page at the price list.
  buttonLabel: string;
  sidebarHeadline: string;
  sidebarButton: string;
  barTitle: string;
  barButton: string;
  // Each city's removal page with the price list in view, or null for screening
  // (those cards link the city landing page, as before).
  cities: CtaCity[] | null;
};

const SCREENING_HEADLINE = "Nie zgaduj ze zdjęć — zbadaj swoje znamiona";

const removalCities = (preferSurgery: boolean): CtaCity[] =>
  LOCATIONS.filter((location) => location.type === CURRENT).map((location) => {
    const slug = location.link.split("/")[0];
    const page = preferSurgery || NO_LASER.has(slug) ? SURGICAL_PAGE : LASER_PAGE;
    return { city: location.city, to: `/${slug}/${page}#cennik` };
  });

const isIntent = (value: unknown): value is CtaIntent =>
  value === "screening" || value === "lesion" || value === "removal" || value === "aftercare";

// `override` is the post's frontmatter `cta:`. The category is only the
// default: a reader of "czarna kropka na skórze" (benign-lesion category) does
// not know what the spot is and should be asked to get it examined, while a
// reader of "kaszak na twarzy" wants it removed. Each post decides for itself.
export const articleCtaFor = (
  category?: string | null,
  pathname = "",
  override?: string | null
): ArticleCta => {
  const intent: CtaIntent = isIntent(override)
    ? override
    : INTENT_BY_CATEGORY[category || ""] || "screening";

  if (intent === "screening") {
    return {
      intent,
      overline: "Centrum Badania Znamion",
      statsHeadline: SCREENING_HEADLINE,
      closingHeadline: "Zbadaj znamiona w swoim mieście",
      buttonLabel: "Umów badanie znamion",
      sidebarHeadline: SCREENING_HEADLINE,
      sidebarButton: "Umów badanie",
      barTitle: "Zbadaj swoje znamiona",
      barButton: "Umów się",
      cities: null,
    };
  }

  if (intent === "lesion") {
    const headline = "Niepokoi Cię zmiana na skórze? Lekarz oceni ją w powiększeniu";
    return {
      intent,
      overline: "Ocena zmiany skórnej",
      statsHeadline: headline,
      closingHeadline: "Pokaż zmianę lekarzowi w swoim mieście",
      buttonLabel: "Umów ocenę zmiany",
      sidebarHeadline: headline,
      sidebarButton: "Umów ocenę zmiany",
      barTitle: "Pokaż zmianę lekarzowi",
      barButton: "Umów się",
      cities: null,
    };
  }

  const cities = removalCities(SURGICAL_TOPIC.test(pathname));

  if (intent === "removal") {
    return {
      intent,
      overline: "Usuwanie zmian skórnych",
      statsHeadline: "Lekarz oceni zmianę i dobierze metodę usunięcia",
      closingHeadline: "Usuń zmianę w swoim mieście",
      buttonLabel: "Umów usunięcie zmiany",
      sidebarHeadline: "Lekarz oceni zmianę i dobierze metodę usunięcia",
      sidebarButton: "Umów usunięcie",
      barTitle: "Usuń zmianę skórną",
      barButton: "Umów",
      cities,
    };
  }

  return {
    intent,
    overline: "Usuwanie zmian skórnych",
    statsHeadline: "Usuwamy zmiany skórne tak, by blizna była jak najmniejsza",
    closingHeadline: "Usuń zmianę w swoim mieście",
    buttonLabel: "Umów zabieg",
    sidebarHeadline: "Usuwamy zmiany tak, by blizna była jak najmniejsza",
    sidebarButton: "Umów zabieg",
    barTitle: "Usuń zmianę z małą blizną",
    barButton: "Umów",
    cities,
  };
};
